import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LIMA_LOGO } from "../components/LimaHeader";
import { ShieldCheck, ArrowRight, CheckCircle2, Upload, Loader2, FileImage, Search, AlertTriangle } from "lucide-react";

export default function Checklist() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [alert, setAlert] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    razao_social: "", cnpj: "", empregados: 0,
    nome_fantasia: "", endereco: "",
  });

  const start = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password || !form.razao_social || !form.cnpj) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Senha precisa ter no mínimo 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/public/quick-start", {
        name: form.name,
        email: form.email,
        password: form.password,
        razao_social: form.razao_social,
        cnpj: form.cnpj,
        empregados: Number(form.empregados) || 0,
        nome_fantasia: form.nome_fantasia,
        endereco: form.endereco,
      });
      localStorage.setItem("lima_token", data.token);
      toast.success("Vamos começar seu diagnóstico!");
      window.location.href = `/diagnostico/${data.company_id}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao iniciar diagnóstico");
    } finally { setLoading(false); }
  };

  const lookupCnpj = async (cnpjValue) => {
    const digits = (cnpjValue || form.cnpj || "").replace(/\D/g, "");
    if (digits.length !== 14) {
      toast.error("Digite o CNPJ com 14 dígitos");
      return;
    }
    setLookingUp(true);
    setAlert(null);
    try {
      const { data } = await api.get(`/public/lookup-cnpj/${digits}`);
      setForm(f => ({
        ...f,
        cnpj: data.cnpj || f.cnpj,
        razao_social: data.razao_social || f.razao_social,
        nome_fantasia: data.nome_fantasia || f.nome_fantasia,
        endereco: data.endereco || f.endereco,
      }));
      if (data.alert) {
        setAlert(data.alert);
        toast.error(data.alert);
      } else {
        toast.success(`Empresa encontrada: ${data.razao_social}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "CNPJ não encontrado");
    } finally { setLookingUp(false); }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error("Envie imagem JPG, PNG ou WEBP");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo maior que 10MB");
      return;
    }
    setPreview(URL.createObjectURL(file));
    setExtracting(true);
    setAlert(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/public/extract-cnpj", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({
        ...f,
        razao_social: data.razao_social || f.razao_social,
        cnpj: data.cnpj || f.cnpj,
        nome_fantasia: data.nome_fantasia || f.nome_fantasia,
        endereco: data.endereco || f.endereco,
      }));
      toast.success("Dados extraídos! Consultando Receita...");
      // Verify with Receita in background
      if (data.cnpj) {
        setTimeout(() => lookupCnpj(data.cnpj), 300);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não consegui ler a imagem. Preencha manualmente.");
    } finally { setExtracting(false); }
  };

  return (
    <div className="min-h-screen bg-[#f4f6f8]">
      {/* Simplified header (no auth links) */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LIMA_LOGO} alt="Lima SST" className="h-12 w-auto" />
            <div className="hidden md:block border-l border-slate-200 pl-3 ml-1">
              <div className="text-[10px] tracking-[0.28em] text-slate-500 font-semibold">CHECKLIST PÚBLICO</div>
              <div className="font-display font-black text-sm text-[#10243f] leading-tight">Diagnóstico rápido de SST</div>
            </div>
          </div>
          <a href="/login" className="text-sm text-slate-600 hover:text-[#10243f]" data-testid="checklist-login-link">
            Já tenho conta
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-5 gap-10">
        {/* Left copy */}
        <div className="lg:col-span-2">
          <span className="text-[11px] tracking-[0.28em] font-bold text-[#c7a45a]">DIAGNÓSTICO GRATUITO</span>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-[#10243f] mt-2 leading-tight">
            Onde sua empresa está exposta a multas do MTE?
          </h1>
          <p className="text-slate-600 mt-4">
            Responda um checklist adaptativo em cerca de 10 minutos. Um consultor Lima SST
            analisa suas respostas e retorna com o mapa de exposição e o plano de mitigação.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-slate-700">
            {[
              "Motor adaptativo — só as NRs pertinentes ao seu negócio",
              "Análise técnica presencial pelo consultor Lima",
              "Sem custo. Sem compromisso. LGPD compliant.",
            ].map((it, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 size={18} className="text-[#c7a45a] shrink-0 mt-0.5" />
                <span>{it}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 p-4 bg-[#10243f]/5 border border-[#10243f]/10 rounded-md flex gap-3">
            <ShieldCheck className="text-[#c7a45a] shrink-0" size={22} />
            <p className="text-xs text-slate-700">
              Seus dados ficam salvos e você pode continuar depois usando o mesmo email e senha.
              Nada será divulgado sem sua autorização.
            </p>
          </div>
        </div>

        {/* Right form */}
        <div className="lg:col-span-3">
          <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-[0_20px_60px_-20px_rgba(16,36,63,0.15)]">
            <h2 className="font-display font-bold text-xl text-[#10243f] mb-1">Vamos começar</h2>
            <p className="text-sm text-slate-500 mb-6">Precisamos de alguns dados básicos para salvar seu diagnóstico.</p>

            <form onSubmit={start} className="space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#c7a45a] font-bold mb-2">Seus dados</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nome completo *</Label>
                    <Input required data-testid="qs-name" value={form.name}
                           onChange={e => setForm({...form, name: e.target.value})} />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input required type="email" data-testid="qs-email" value={form.email}
                           onChange={e => setForm({...form, email: e.target.value})} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Senha (para acessar depois) *</Label>
                    <Input required type="password" minLength={6} data-testid="qs-password" value={form.password}
                           onChange={e => setForm({...form, password: e.target.value})} />
                    <p className="text-xs text-slate-500 mt-1">Mínimo 6 caracteres. Usada para retomar depois.</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#c7a45a] font-bold">Sua empresa</div>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                         onChange={onFileChange} className="hidden" data-testid="cnpj-file-input" />
                  <Button type="button" variant="outline" size="sm"
                          onClick={() => fileRef.current?.click()}
                          disabled={extracting}
                          data-testid="cnpj-upload-btn"
                          className="border-[#c7a45a] text-[#10243f] hover:bg-[#c7a45a]/10">
                    {extracting ? (
                      <><Loader2 size={14} className="mr-1.5 animate-spin" /> Lendo cartão...</>
                    ) : (
                      <><Upload size={14} className="mr-1.5" /> Enviar foto do cartão CNPJ / CCMEI</>
                    )}
                  </Button>
                </div>
                {preview && (
                  <div className="mb-4 flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-md">
                    <img src={preview} alt="Cartão CNPJ" className="w-20 h-20 object-cover rounded border border-slate-300" />
                    <div className="flex-1">
                      <div className="text-xs font-semibold text-[#10243f] flex items-center gap-1">
                        <FileImage size={12} /> Imagem enviada
                      </div>
                      <div className="text-[11px] text-slate-500">Os campos abaixo foram preenchidos automaticamente. Confira e ajuste se necessário.</div>
                    </div>
                  </div>
                )}
                {alert && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2" data-testid="cnpj-alert">
                    <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={16} />
                    <div className="text-xs text-red-800">{alert}</div>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Razão social *</Label>
                    <Input required data-testid="qs-razao" value={form.razao_social}
                           onChange={e => setForm({...form, razao_social: e.target.value})} />
                  </div>
                  <div>
                    <Label>CNPJ *</Label>
                    <Input required data-testid="qs-cnpj" placeholder="00.000.000/0000-00"
                           value={form.cnpj}
                           onChange={e => setForm({...form, cnpj: e.target.value})} />
                  </div>
                  <div>
                    <Label>Nº de empregados</Label>
                    <Input type="number" min="0" data-testid="qs-empregados" value={form.empregados}
                           onChange={e => setForm({...form, empregados: e.target.value})} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Nome fantasia (opcional)</Label>
                    <Input data-testid="qs-fantasia" value={form.nome_fantasia}
                           onChange={e => setForm({...form, nome_fantasia: e.target.value})} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Endereço (opcional)</Label>
                    <Input data-testid="qs-endereco" value={form.endereco}
                           onChange={e => setForm({...form, endereco: e.target.value})} />
                  </div>
                </div>
              </div>

              <Button type="submit" disabled={loading}
                      data-testid="qs-submit"
                      className="w-full bg-[#c7a45a] hover:bg-[#b58f43] text-[#10243f] font-bold h-12 text-base">
                {loading ? "Iniciando..." : "Iniciar diagnóstico"} <ArrowRight size={18} className="ml-2" />
              </Button>

              <p className="text-[11px] text-slate-500 text-center">
                Ao iniciar, você concorda com o tratamento dos dados conforme LGPD
                para fins de análise técnica de SST.
              </p>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
